import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Stack,
  Divider,
  Alert,
  FormControlLabel,
  Switch,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";

const FlipSignModeSettingsDialog = ({
  open,
  onClose,
  currentFlipSignMode = {},
  onSave,
  sections = ["Revenue", "Other Income", "Cost of Sales", "Expenses"],
}) => {
  const [flipSignMode, setFlipSignMode] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (open && !isInitialized) {
      // Only initialize when dialog first opens
      setFlipSignMode({ ...currentFlipSignMode });
      setIsInitialized(true);
    } else if (!open) {
      // Reset initialization flag when dialog closes
      setIsInitialized(false);
    }
  }, [open, currentFlipSignMode, isInitialized]);

  const handleToggleSign = (section) => {
    setFlipSignMode((prev) => ({
      ...prev,
      [section]: prev[section] === "positive" ? "negative" : "positive",
    }));
  };

  const handleSave = () => {
    onSave(flipSignMode);
    onClose();
  };

  const handleClose = () => {
    setFlipSignMode({ ...currentFlipSignMode });
    onClose();
  };

  const hasChanges = () => {
    return JSON.stringify(flipSignMode) !== JSON.stringify(currentFlipSignMode);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle sx={{ p: 3 }}>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Box display='flex' alignItems='center' gap={1}>
            <SettingsIcon color='primary' />
            <Typography variant='h6' fontWeight={600}>
              Section Sign Mode Settings
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Alert severity='info' sx={{ borderRadius: 2 }}>
            <Typography variant='body2'>
              Configure how amounts are displayed and calculated for each
              section. Positive mode shows amounts as positive values, while
              negative mode shows them as negative values.
            </Typography>
          </Alert>

          <Box>
            <Typography variant='subtitle1' fontWeight={600} gutterBottom>
              Section Sign Modes
            </Typography>

            <Stack spacing={2}>
              {sections.map((section) => (
                <Box
                  key={section}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant='subtitle2' fontWeight={600}>
                      {section}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {flipSignMode[section] === "positive"
                        ? "Amounts displayed as positive values"
                        : "Amounts displayed as negative values"}
                    </Typography>
                  </Box>
                  <Box display='flex' alignItems='center' gap={2}>
                    <Chip
                      label={
                        flipSignMode[section] === "positive"
                          ? "Positive"
                          : "Negative"
                      }
                      color={
                        flipSignMode[section] === "positive"
                          ? "success"
                          : "error"
                      }
                      variant='outlined'
                      size='small'
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={flipSignMode[section] === "negative"}
                          onChange={() => handleToggleSign(section)}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color:
                                flipSignMode[section] === "positive"
                                  ? "#2e7d32"
                                  : "#c62828",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor:
                                  flipSignMode[section] === "positive"
                                    ? "#a5d6a7"
                                    : "#ffcdd2",
                              },
                            "& .MuiSwitch-switchBase": {
                              color:
                                flipSignMode[section] === "positive"
                                  ? "#388e3c"
                                  : "#d32f2f",
                            },
                            "& .MuiSwitch-track": {
                              backgroundColor:
                                flipSignMode[section] === "positive"
                                  ? "#c8e6c9"
                                  : "#ffe5e5",
                            },
                          }}
                        />
                      }
                      label=''
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant='subtitle2' fontWeight={600} gutterBottom>
              How it works:
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              • <strong>Positive Mode:</strong> Amounts are displayed and
              calculated as positive values
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              • <strong>Negative Mode:</strong> Amounts are displayed and
              calculated as negative values
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              • Changes affect both display and calculation of section totals
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              • Net profit calculation automatically adjusts based on these
              settings
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} color='inherit'>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant='contained'
          disabled={!hasChanges()}
          startIcon={<CompareArrowsIcon />}
        >
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlipSignModeSettingsDialog;
